import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { ValidationFormat } from '@wix/ambassador-forms-v4-form/types';
import { getCountryByShortKey } from '@wix/locale-dataset-javascript';
import { ADDRESS_FORM_ID_BY_COUNTRY, DEFAULT_COUNTRY } from './constants';
import { applyValidationProperties } from './apply-validation-properties';
import { applyViewProperties } from './apply-view-properties';
import { getCountryField } from './get-country-field';
import { COUNTRY_CODES } from './country-codes';
import { mapSubdivisionReplacedBy } from './map-subdivision-replaced-by';
export class MultilineAddressToolkit {
  constructor(addressForms, addressFormIdByCountry) {
    if (addressFormIdByCountry === void 0) {
      addressFormIdByCountry = ADDRESS_FORM_ID_BY_COUNTRY;
    }
    _defineProperty(this, "addressFormByCountry", void 0);
    /**
     * Normalizes empty values in submission to empty objects
     * This is needed to pass validation when address is required
     * @param submission - submission to normalize
     * @param form - form to get fields from
     */
    _defineProperty(this, "normalizeEmptyValues", (submission, form) => {
      return Object.keys(submission).reduce((acc, key) => {
        var _form$fields, _field$validation, _field$validation2;
        const field = (_form$fields = form.fields) == null ? void 0 : _form$fields.find(_field => _field.target === key);
        if (field != null && (_field$validation = field.validation) != null && _field$validation.required && !(field != null && field.hidden) && (field == null || (_field$validation2 = field.validation) == null || (_field$validation2 = _field$validation2.predefined) == null ? void 0 : _field$validation2.format) === ValidationFormat.MULTILINE_ADDRESS) {
          acc[key] = submission[key] || {};
        } else {
          acc[key] = submission[key];
        }
        return acc;
      }, {});
    });
    _defineProperty(this, "addCountryField", (form, rootFormField) => {
      var _rootFormField$valida, _rootFormField$valida2;
      return {
        ...form,
        fields: [getCountryField(((_rootFormField$valida = rootFormField.validation) == null ? void 0 : _rootFormField$valida.required) && !(rootFormField != null && rootFormField.hidden), (_rootFormField$valida2 = rootFormField.validation) == null || (_rootFormField$valida2 = _rootFormField$valida2.predefined) == null ? void 0 : _rootFormField$valida2.multilineAddressOptions), ...(form.fields ?? [])]
      };
    });
    _defineProperty(this, "addSubdivisions", (form, countryCode) => {
      var _country$subdivisions, _form$fields2;
      if (!countryCode) {
        return form;
      }
      const country = getCountryByShortKey(countryCode);
      const countryCodeAlpha3 = countryCode && countryCode in COUNTRY_CODES ? COUNTRY_CODES[countryCode] : undefined;
      const subdivisions = mapSubdivisionReplacedBy((country == null || (_country$subdivisions = country.subdivisions) == null ? void 0 : _country$subdivisions.list) || [], countryCodeAlpha3);
      if (!(subdivisions != null && subdivisions.length)) {
        return form;
      }
      return {
        ...form,
        fields: (_form$fields2 = form.fields) == null ? void 0 : _form$fields2.map(field => {
          if ((field == null ? void 0 : field.target) === 'subdivision') {
            var _field$validation3;
            return {
              ...field,
              view: {
                ...field.view,
                options: subdivisions.map(subdivision => ({
                  value: subdivision.key,
                  label: subdivision.displayName
                }))
              },
              validation: {
                ...field.validation,
                string: {
                  ...((_field$validation3 = field.validation) == null ? void 0 : _field$validation3.string),
                  enum: subdivisions.map(subdivision => subdivision.key)
                }
              }
            };
          }
          return field;
        })
      };
    });
    /**
     * Adds hardcoded country field, which is needed to build validation.
     * Address forms themselves not contain country field
     * @param rootFormField - root form field
     * @param submissionValue - value of submission, to get country info
     */
    _defineProperty(this, "getFormForValidation", (rootFormField, submissionValue) => {
      const country = this.getCountry(submissionValue);
      return this.getFormByCountry(country, rootFormField);
    });
    this.addressFormByCountry = this.mapFormsByCountry(addressFormIdByCountry, addressForms);
  }
  getFormByCountry(country, rootFormField) {
    this.assertMultiLineAddressField(rootFormField);
    const addressForm = country && this.addressFormByCountry[country] ? this.addressFormByCountry[country] : this.addressFormByCountry[DEFAULT_COUNTRY];
    if (!addressForm) {
      throw new Error(`No form suitable fo country: ${country}`);
    }
    return rootFormField ? this.addCountryField(this.addSubdivisions(this.applyRootFormSettingsOverrides(addressForm, rootFormField), country), rootFormField) : addressForm;
  }
  applyRootFormSettingsOverrides(addressForm, rootFormField) {
    var _rootFormField$valida3;
    const modifications = rootFormField ? [applyViewProperties(rootFormField.view), applyValidationProperties((_rootFormField$valida3 = rootFormField.validation) == null || (_rootFormField$valida3 = _rootFormField$valida3.predefined) == null ? void 0 : _rootFormField$valida3.multilineAddressOptions)] : [];
    return modifications.reduce((acc, func) => func(acc), addressForm);
  }
  assertMultiLineAddressField(rootFormField) {
    var _rootFormField$valida4;
    if (rootFormField && ((_rootFormField$valida4 = rootFormField.validation) == null || (_rootFormField$valida4 = _rootFormField$valida4.predefined) == null ? void 0 : _rootFormField$valida4.format) !== ValidationFormat.MULTILINE_ADDRESS) {
      throw new Error(`Only field ${ValidationFormat.MULTILINE_ADDRESS} is supported`);
    }
  }
  mapFormsByCountry(formIdByCountryMap, addressForms) {
    const formsById = addressForms.reduce((acc, form) => {
      acc[form.id] = form;
      return acc;
    }, {});
    return Object.fromEntries(Object.entries(formIdByCountryMap).map(_ref => {
      let [country, formId] = _ref;
      const form = formsById[formId];
      if (form) {
        return [country, form];
      } else {
        throw new Error(`Missing form template for country ${country}`);
      }
    }));
  }
  getCountry(value) {
    if (value && typeof value === 'object' && 'country' in value) {
      return value.country;
    }
    return DEFAULT_COUNTRY;
  }
}
//# sourceMappingURL=multiline-address-toolkit.js.map